import { useEffect, useState } from "react";
import { AIButton, FilledButton, FlatButton, OutlinedButton, TextButton } from "../../common/Button";
import Lightbox from "../../common/Lightbox";
import TextInputField from "../../common/TextInputField";
import TextInputArea from "../../common/TextInputArea";
import { isProjectBriefDescriptionValid, isProjectDescriptionValid, isProjectExpectedDeliveryValid, isProjectSkillsValid, isProjectTitleValid, isProjectToolsValid, projectBriefDescriptionMaxCharCount, projectBriefDescriptionMinCharCount, projectDescriptionMinCharCount, projectExpectedDeliveryMinCharCount } from "../../../utils/projectUtils";
import { IProject } from "../../../data_models/project";
import { useCookies } from "react-cookie";
import ECookies from "../../../config/cookies";
import useDisplayNotifications from "../../../hooks/useDisplayNotifications";
import { DisplayNotificationType } from "../../../models/DisplayNotification";

import DatePicker from "react-datepicker";
import useMyProjects from "../../../hooks/useMyProjects";
import useRouting from "../../../hooks/useRouting";
import InfoBox from "../../common/InfoBox";
import InfoTooltip from "../../common/InfoTooltip";
import MultiTextSelect from "../../common/MultiTextSelect";
import useCurrentUser from "../../../hooks/useCurrentUser";
import { EUserType } from "../../../data_models/enums";
import RichTextEditor from "../../common/RichTextEditor";
import RichText from "../../common/RichText";
import useProjects from "../../../hooks/useProjects";
import { IoIosColorWand } from "react-icons/io";
import AISuggestion from "../../common/AISuggestion";


interface EditOrCreateProjectLightboxProps {
    onClose: () => void;
    project?: IProject;
    onProjectUpdate?: (updatedProject: IProject) => void;
}

const EditOrCreateProjectLightbox: React.FC<EditOrCreateProjectLightboxProps> = ({ onClose, project, onProjectUpdate }) => {

    const { currentUserType } = useCurrentUser();

    const { suggestRefinedProjectDetails,
        potentialProjectDetailDefinitionWeaknesses } = useProjects();

    const [title, setTitle] = useState(project?.title || '');
    const [briefDescription, setBriefDescription] = useState(project?.briefDescription || '');
    const [projectDescription, setProjectDescription] = useState(project?.description || '');
    const [projectExpectedDelivery, setProjectExpectedDelivery] = useState(project?.expectedDelivery || '');
    const [projectSkills, setProjectSkills] = useState(project?.requiredSkills || [] as string[]);
    const [projectTools, setProjectTools] = useState(project?.requiredTools || [] as string[]);
    const [projectStartDate, setProjectStartDate] = useState<Date | undefined>(project?.startDate || undefined);
    const [projectDueDate, setProjectDueDate] = useState<Date | undefined>(project?.dueDate || undefined);
    const [projectRisks, setProjectRisks] = useState(project?.risks || '');

    const [suggestRefinedProjectDetailsLoading, setSuggestRefinedProjectDetailsLoading] = useState(false);


    const [refinedTitle, setRefinedTitle] = useState<string | undefined>(undefined);
    const [refinedBriefDescription, setRefinedBriefDescription] = useState<string | undefined>(undefined);
    const [refinedDetailedDescription, setRefinedDetailedDescription] = useState<string | undefined>(undefined);
    const [refinedExpectedDelivery, setRefinedExpectedDelivery] = useState<string | undefined>(undefined);
    const [refinedSkills, setRefinedSkills] = useState<string[] | undefined>(undefined);
    const [refinedTools, setRefinedTools] = useState<string[] | undefined>(undefined);
    const [refinedRisks, setRefinedRisks] = useState<string | undefined>(undefined);

    const [showSuggestRefinedProjectDetailsWarningMessage, setShowSuggestRefinedProjectDetailsWarningMessage] = useState<string | undefined>(undefined);
    const [canTriggerSuggestRefinedProjectDetails, setCanTriggerSuggestRefinedProjectDetails] = useState(true);

    const [loading, setLoading] = useState(false);
    const [createButtonActive, setCreateButtonActive] = useState(false);

    const infoTitle = 'Provide a clear, concise, and compelling project title that reflects the project’s goal, scope, and key focus areas in a few words';
    const infoStartDate = 'Set a start date, and the project manager will do their best to begin on time. If left blank, the project will start as soon as possible.';
    const infoDueDate = 'Set a deadline, and the project manager will aim to meet it. If left blank, the project will be completed as soon as possible.';
    const infoBriefDescription = 'Write a brief but comprehensive project summary covering objectives, scope, milestones, timeline, and any constraints. This should serve as a foundation for a detailed project plan which will be created by the project manager.';
    const infoDescription = 'Write a detailed project description that includes the project’s objectives, scope, key deliverables, milestones, stakeholders, roles and responsibilities, timeline, risks, dependencies, and success criteria. Ensure the description is structured clearly to guide execution and stakeholder alignment.';
    const infoExpectedDelivery = 'List the expected project deliverables, specifying the files, formats, products, or outcomes that will be delivered upon project completion.';
    const infoSkills = 'List the key skills and expertise required to successfully complete the project.';
    const infoTools = 'List the essential tools, software, and technologies required to successfully execute the project.';
    const infoRisks = 'Identify potential risks, challenges, or pitfalls related to the given description. These may include unclear objectives, unrealistic timelines, scope creep, resource limitations, or any other factors that could impact successful project completion';

    const [cookies, setCookie, removeCookie] = useCookies();
    const { addNotification } = useDisplayNotifications();

    const { createProjectDraft, updateProjectDetails } = useMyProjects();

    const { goToProjectDetails } = useRouting();

    const [briefDescriptionCharacters, setBriefDescriptionCharacters] = useState(0);
    const [projectDescriptionCharacters, setProjectDescriptionCharacters] = useState(0);
    const [projectExpectedDeliveryCharacters, setProjectExpectedDeliveryCharacters] = useState(0);

    useEffect(() => {
        if (project) {
            setTitle(project.title || '');
            setBriefDescription(project.briefDescription || '');
            setProjectDescription(project.description || '');
            setProjectExpectedDelivery(project.expectedDelivery || '');
            setProjectSkills(project.requiredSkills || [] as string[]);
            setProjectTools(project.requiredTools || [] as string[]);
            setProjectStartDate(project.startDate || undefined);
            setProjectDueDate(project.dueDate || undefined);


        } else if (cookies[ECookies.CREATE_PROJECT_DATA_COOKIE]) {
            const projectData = cookies[ECookies.CREATE_PROJECT_DATA_COOKIE];
            setTitle(projectData.title);
            setBriefDescription(projectData.briefDescription);
            setProjectDescription(projectData.description);
            setProjectExpectedDelivery(projectData.expectedDelivery);
            setProjectSkills(projectData.requiredSkills as string[]);
            setProjectTools(projectData.requiredTools as string[]);
        }

        if (cookies[ECookies.SUGGEST_REFINED_PROJECT_DETAILS_COOKIE] &&
            project?._id &&
            project?._id === cookies[ECookies.SUGGEST_REFINED_PROJECT_DETAILS_COOKIE].projectId) {
            const refinedProjectDetails = cookies[ECookies.SUGGEST_REFINED_PROJECT_DETAILS_COOKIE];
            setRefinedTitle(refinedProjectDetails.title);
            setRefinedBriefDescription(refinedProjectDetails.briefDescription);
            setRefinedDetailedDescription(refinedProjectDetails.description);
            setRefinedExpectedDelivery(refinedProjectDetails.expectedDelivery);
            setRefinedSkills(refinedProjectDetails.requiredSkills);
            setRefinedTools(refinedProjectDetails.requiredTools);
            setRefinedRisks(refinedProjectDetails.risks);
        } else {
            removeCookie(ECookies.SUGGEST_REFINED_PROJECT_DETAILS_COOKIE);
        }
    }, []);


    useEffect(() => {
        const currentCookie = cookies[ECookies.SUGGEST_REFINED_PROJECT_DETAILS_COOKIE];
        setCookie(ECookies.SUGGEST_REFINED_PROJECT_DETAILS_COOKIE,
            JSON.stringify({
                projectId: project?._id,
                title: refinedTitle || currentCookie?.title,
                briefDescription: refinedBriefDescription || currentCookie?.briefDescription,
                description: refinedDetailedDescription || currentCookie?.description,
                expectedDelivery: refinedExpectedDelivery || currentCookie?.expectedDelivery,
                requiredSkills: refinedSkills || currentCookie?.requiredSkills,
                requiredTools: refinedTools || currentCookie?.requiredTools,
                risks: refinedRisks || currentCookie?.risks
            }),
            { path: '/', secure: true, sameSite: 'strict' });
    }, [refinedTitle,
        refinedBriefDescription,
        refinedDetailedDescription,
        refinedExpectedDelivery,
        refinedSkills,
        refinedTools,
        refinedRisks]);

    useEffect(() => {
        validateForm();
        if (!project) {
            setCookieData();
        }
    }, [title,
        briefDescription,
        projectDescription,
        projectExpectedDelivery,
        projectSkills,
        projectTools,
        projectRisks]);

    const validateForm = () => {

        if (!isProjectTitleValid(title)) {
            setCreateButtonActive(false);
            return;
        }

        if (!isProjectDescriptionValid(projectDescription)) {
            setCreateButtonActive(false);
            return;
        }
        if (!isProjectExpectedDeliveryValid(projectExpectedDelivery)) {
            setCreateButtonActive(false);
            return;
        }
        if (!isProjectSkillsValid(projectSkills)) {
            setCreateButtonActive(false);
            return;
        }
        if (!isProjectToolsValid(projectTools)) {
            setCreateButtonActive(false);
            return;
        }

        setCreateButtonActive(true);
    }

    const setCookieData = () => {
        setCookie(ECookies.CREATE_PROJECT_DATA_COOKIE, JSON.stringify({
            title: title,
            startDate: projectStartDate,
            dueDate: projectDueDate,
            briefDescription: briefDescription,
            description: projectDescription,
            expectedDelivery: projectExpectedDelivery,
            requiredSkills: projectSkills,
            requiredTools: projectTools,
            risks: projectRisks,
        }), { path: '/', secure: true, sameSite: 'strict' });
    }


    const onCreateOrUpdate = async (onlyUpdate: boolean = true) => {
        try {
            setLoading(true);

            // if project was not given to this component, create a new project draft
            // else update the project draft
            if (!project) {
                const projectData = cookies[ECookies.CREATE_PROJECT_DATA_COOKIE];
                const proj: IProject = {
                    title: projectData.title,
                    startDate: projectData.startDate ? new Date(projectData.startDate) : undefined,
                    dueDate: projectData.dueDate ? new Date(projectData.dueDate) : undefined,
                    briefDescription: projectData.briefDescription,
                    description: projectData.description,
                    expectedDelivery: projectData.expectedDelivery,
                    requiredSkills: projectData.requiredSkills,
                    requiredTools: projectData.requiredTools,
                    risks: projectData.risks,
                };

                const newProject = await createProjectDraft(proj);
                removeCookie(ECookies.CREATE_PROJECT_DATA_COOKIE);
                onClose();
                goToProjectDetails(newProject._id || '');
            } else {
                const proj: IProject = {
                    _id: project._id,
                    title: title,
                    startDate: projectStartDate,
                    dueDate: projectDueDate,
                    briefDescription: briefDescription,
                    description: projectDescription,
                    expectedDelivery: projectExpectedDelivery,
                    requiredSkills: projectSkills,
                    requiredTools: projectTools,
                    risks: projectRisks,
                };

                const updatedProject = await updateProjectDetails(proj);
                if (onProjectUpdate) {
                    onProjectUpdate(updatedProject);
                }
                onClose();
                goToProjectDetails(updatedProject._id || '');
            }


        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            }, true);
        } finally {
            if (onlyUpdate) {
                setLoading(false);
            }
        }
    };

    const onClear = () => {
        setTitle('');
        setBriefDescription('');
        setProjectDescription('');
        setProjectExpectedDelivery('');
        setProjectSkills([]);
        setProjectTools([]);
        setProjectRisks('');
    }

    const onSuggestRefinedProjectDetailsIntent = async () => {
        if (!project?.briefDescription) {
            setShowSuggestRefinedProjectDetailsWarningMessage('To get the best results, try to include as much detail as possible. Even a brief description with key information will be really helpful! You can update your details and try again.');
            setCanTriggerSuggestRefinedProjectDetails(false);
        } else {
            setShowSuggestRefinedProjectDetailsWarningMessage('To get the best results, try to include as much detail as possible. Even a brief description with key information will be really helpful!');
            setCanTriggerSuggestRefinedProjectDetails(true);
        }

    }

    const onSuggestRefinedProjectDetails = async () => {
        try {
            setSuggestRefinedProjectDetailsLoading(true);


            const refinedProjectDetails = await suggestRefinedProjectDetails(project?._id || '');
            setShowSuggestRefinedProjectDetailsWarningMessage(undefined);

            setRefinedTitle(refinedProjectDetails.title);
            setRefinedBriefDescription(refinedProjectDetails.briefDescription);
            setRefinedDetailedDescription(refinedProjectDetails.description);
            setRefinedExpectedDelivery(refinedProjectDetails.expectedDelivery);
            setRefinedSkills(refinedProjectDetails.requiredSkills);
            setRefinedTools(refinedProjectDetails.requiredTools);
            setRefinedRisks(refinedProjectDetails.risks);
        } catch (error: any) {
            addNotification({
                message: error.message,
                type: DisplayNotificationType.ERROR
            }, true);
        } finally {
            setSuggestRefinedProjectDetailsLoading(false);
        }
    }

    const onUseSuggestedTitle = (accept: boolean) => {
        if (accept) {
            const title = refinedTitle || '';
            setTitle(title);
            setRefinedTitle(undefined);
        } else {
            setRefinedTitle(project?.title || '');
        }
    }

    const onUseSuggestedBriefDescription = (accept: boolean) => {
        if (accept) {
            const briefDescription = refinedBriefDescription || '';
            setBriefDescription(briefDescription);
            setRefinedBriefDescription(undefined);
        } else {
            setRefinedBriefDescription(project?.briefDescription || '');
        }
    }

    const onUseSuggestedDetailedDescription = (accept: boolean) => {
        if (accept) {
            const description = refinedDetailedDescription || '';
            setProjectDescription(description);
            setRefinedDetailedDescription(undefined);
        } else {
            setRefinedDetailedDescription(project?.description || '');
        }
    }

    const onUseSuggestedExpectedDelivery = (accept: boolean) => {
        if (accept) {
            const expectedDelivery = refinedExpectedDelivery || '';
            setProjectExpectedDelivery(expectedDelivery);
            setRefinedExpectedDelivery(undefined);
        } else {
            setRefinedExpectedDelivery(project?.expectedDelivery || '');
        }
    }

    const onUseSuggestedSkills = (accept: boolean) => {
        if (accept) {
            const skills = refinedSkills || [];
            setProjectSkills(skills);
            setRefinedSkills(undefined);
        } else {
            setRefinedSkills(project?.requiredSkills || []);
        }
    }

    const onUseSuggestedTools = (accept: boolean) => {
        if (accept) {
            const tools = refinedTools || [];
            setProjectTools(tools);
            setRefinedTools(undefined);
        } else {
            setRefinedTools(project?.requiredTools || []);
        }
    }

    const onUseSuggestedRisks = (accept: boolean) => {
        if (accept) {
            const risks = refinedRisks || '';
            setProjectRisks(risks);
            setRefinedRisks(undefined);
        } else {
            setRefinedRisks(project?.risks || '');
        }
    }


    return (

        <Lightbox
            content={
                <div className="flex flex-col items-start space-y-4" >
                    <div className="w-full flex flex-row items-center justify-between gap-2">
                        <h3 className="font-bold">Edit Project</h3>
                        {!showSuggestRefinedProjectDetailsWarningMessage && <AIButton
                            onClick={onSuggestRefinedProjectDetailsIntent}
                            icon={<IoIosColorWand />}
                        >Suggest Refined Details</AIButton>}
                    </div>

                    {showSuggestRefinedProjectDetailsWarningMessage && <>
                        <p>{showSuggestRefinedProjectDetailsWarningMessage}</p>
                        <div className="w-full flex flex-row items-center justify-end gap-2">
                            <OutlinedButton
                                onClick={() => {
                                    setShowSuggestRefinedProjectDetailsWarningMessage(undefined);
                                }}
                            >Go Back</OutlinedButton>
                            {canTriggerSuggestRefinedProjectDetails && <AIButton
                                onClick={onSuggestRefinedProjectDetails}
                                icon={<IoIosColorWand />}
                                isLoading={suggestRefinedProjectDetailsLoading}
                            >Suggest Refined Details</AIButton>}
                        </div>
                    </>}

                    {!showSuggestRefinedProjectDetailsWarningMessage && <>
                        {/* Title */}
                        <InfoBox>
                            <p className="caption1">Start by creating a project draft. You can update the details anytime. Drafts stay private and won't be processed until you publish them. </p>
                        </InfoBox>

                        <div className="w-full">
                            <div className="flex items-center gap-2">
                                <label>Title</label>
                                <InfoTooltip text={infoTitle} />
                            </div>
                            <TextInputField
                                value={title}
                                onChange={(e) => {
                                    setTitle(e.target.value);
                                }}
                                placeholder="Project Title"
                            />

                            {refinedTitle && project?.title !== refinedTitle && <div className="mt-1">
                                <AISuggestion
                                    onAccept={() => onUseSuggestedTitle(true)}
                                    header='Suggested Title'
                                >
                                    <span className="caption2">{refinedTitle}</span>
                                </AISuggestion>
                            </div>}
                        </div>

                        {/* Start Date and Due Date */}
                        <div className="w-full">
                            <div className="w-full flex">
                                <div className="w-1/2">
                                    <div className="flex items-center gap-2">
                                        <label>Start Date</label>
                                        <InfoTooltip text={infoStartDate} />
                                    </div>

                                    <DatePicker
                                        selected={projectStartDate}
                                        onChange={(date: Date | null) => setProjectStartDate(date ? date : undefined)}
                                        placeholderText="Start Date"
                                        className="w-full"
                                    />

                                </div>
                                <div className="w-1/2">
                                    <div className="flex items-center gap-2">
                                        <label>Due Date</label>
                                        <InfoTooltip text={infoDueDate} />
                                    </div>
                                    <DatePicker
                                        selected={projectDueDate}
                                        onChange={(date: Date | null) => setProjectDueDate(date ? date : undefined)}
                                        placeholderText="Due Date"
                                        className="w-full"
                                    />

                                </div>
                            </div>
                        </div>

                        {/* Employers will edit the brief description */}
                        {/* {currentUserType === EUserType.EMPLOYER && <div className="w-full">
                            <div className="flex items-center gap-2">
                                <label>Brief Description</label>
                                <InfoTooltip text={infoBriefDescription} />
                            </div>

                            <RichTextEditor
                                value={briefDescription}
                                onChange={(content) => { setBriefDescription(content); }}
                                setCountCharacters={(count) => { setBriefDescriptionCharacters(count); }} />

                            <div className="flex flex-row w-full justify-end">
                                <span className="caption2">{briefDescriptionCharacters}</span>
                                <span className="ml-1 caption2">
                                    (Min. {projectBriefDescriptionMinCharCount} characters)
                                </span>
                            </div>
                            {refinedBriefDescription && project?.briefDescription !== refinedBriefDescription && <div className="mt-1">
                                <AISuggestion
                                    onAccept={() => onUseSuggestedBriefDescription(true)}
                                    header='Suggested Brief Description'
                                >
                                    <RichText text={refinedBriefDescription} />
                                </AISuggestion>
                            </div>}

                        </div>} */}

                        {/* PMs will edit the detailed description but see detailed brief by client */}
                        {/* {currentUserType === EUserType.FREELANCER &&  */}
                        <div className="w-full">


                            {/* <label className="italic">Brief Description (serves as foundation for detailed description)</label>
                            <div className="flex flex-col space-y-2 bg-neutral-100 p-4 rounded-lg mb-4">
                                <RichText text={briefDescription} />
                            </div> */}



                            <div className="flex items-center gap-2">
                                <label>Detailed Description</label>
                                <InfoTooltip text={infoDescription} />
                            </div>

                            <RichTextEditor
                                value={projectDescription}
                                onChange={(content) => { setProjectDescription(content); }}
                                setCountCharacters={(count) => { setProjectDescriptionCharacters(count); }} />

                            <div className="flex flex-row w-full justify-end">
                                <span className="caption2">{projectDescriptionCharacters}</span>
                                <span className="ml-1 caption2">
                                    (Min. {projectDescriptionMinCharCount} characters)
                                </span>
                            </div>

                            {refinedDetailedDescription && projectDescription !== refinedDetailedDescription && <div className="mt-1">
                                <AISuggestion
                                    onAccept={() => onUseSuggestedDetailedDescription(true)}
                                    header='Suggested Detailed Description'
                                >
                                    <RichText text={refinedDetailedDescription} />
                                </AISuggestion>

                            </div>}
                        </div>
                        {/* } */}

                        {/* Expected Delivery */}
                        <div className="w-full">
                            <div className="flex items-center gap-2">
                                <label>Expected Delivery</label>
                                <InfoTooltip text={infoExpectedDelivery} />
                            </div>

                            <RichTextEditor
                                value={projectExpectedDelivery}
                                onChange={(content) => { setProjectExpectedDelivery(content); }}
                                setCountCharacters={(count) => { setProjectExpectedDeliveryCharacters(count); }} />

                            <div className="flex flex-row w-full justify-end">
                                <span className="caption2">{projectExpectedDeliveryCharacters}</span>
                                <span className="ml-1 caption2">
                                    (Min. {projectExpectedDeliveryMinCharCount} characters)
                                </span>
                            </div>
                            {refinedExpectedDelivery && project?.expectedDelivery !== refinedExpectedDelivery && <div className="mt-1">
                                <AISuggestion
                                    onAccept={() => onUseSuggestedExpectedDelivery(true)}
                                    header='Suggested Expected Delivery'
                                >
                                    <RichText text={refinedExpectedDelivery} />
                                </AISuggestion>
                            </div>}
                        </div>

                        {/* Skills */}
                        <div className="w-full">
                            <div className="flex items-center gap-2">
                                <label>Skills</label>
                                <InfoTooltip text={infoSkills} />
                            </div>

                            <MultiTextSelect
                                elements={projectSkills}
                                setElements={setProjectSkills}
                                placeholder="Skills" />

                            {refinedSkills && !projectSkills.every(skill => refinedSkills.includes(skill)) && <div className="mt-1">
                                <AISuggestion
                                    onAccept={() => onUseSuggestedSkills(true)}
                                    header='Suggested Skills'
                                >
                                    <span className="caption2">{refinedSkills.join(', ')}</span>
                                </AISuggestion>
                            </div>}
                        </div>

                        {/* Tools */}
                        <div className="w-full">
                            <div className="flex items-center gap-2">
                                <label>Tools</label>
                                <InfoTooltip text={infoTools} />
                            </div>
                            <MultiTextSelect
                                elements={projectTools}
                                setElements={setProjectTools}
                                placeholder="Tools" />

                            {refinedTools && !projectTools.every(tool => refinedTools.includes(tool)) && <div className="mt-1">
                                <AISuggestion
                                    onAccept={() => onUseSuggestedTools(true)}
                                    header='Suggested Tools'
                                >
                                    <span className="caption2">{refinedTools.join(', ')}</span>
                                </AISuggestion>
                            </div>}
                        </div>

                        {/* Risks */}
                        <div className="w-full">
                            <div className="flex items-center gap-2">
                                <label>Potential Project Risks</label>
                                <InfoTooltip text={infoRisks} />
                            </div>

                            <RichTextEditor
                                value={projectRisks}
                                onChange={(content) => { setProjectRisks(content); }}
                            />

                            {refinedRisks && project?.risks !== refinedRisks && <div className="mt-1">
                                <AISuggestion
                                    onAccept={() => onUseSuggestedRisks(true)}
                                    header='Suggested Risks'
                                >
                                    <RichText text={refinedRisks} />
                                </AISuggestion>
                            </div>}
                        </div>



                        <div className="flex flex-row w-full space-x-2">
                            <span className="mr-auto">{!project && <FlatButton onClick={onClear}>Clear</FlatButton>}</span>
                            <OutlinedButton onClick={onClose}>Cancel</OutlinedButton>
                            <FilledButton
                                isLoading={loading}
                                disabled={!createButtonActive}
                                onClick={onCreateOrUpdate}>{project ? 'Update' : 'Create'}</FilledButton>
                        </div>

                    </>}
                </div>}
            onClose={onClose}
        />

    );
}

export default EditOrCreateProjectLightbox;